import { useState, useEffect, type FormEvent, type ReactNode } from 'react';
import { Modal, Button, Input, Spinner, Icon, Avatar } from './ui';
import { api, ApiError, setToken } from '../api';
import { ActiveSessions } from './ActiveSessions';
import { TwoFactorSettings } from './TwoFactorSettings';
import { AccountDataSettings } from './AccountDataSettings';
import { getPushState, subscribePush, unsubscribePush, type PushState } from '../push';
import { useApp, THEMES, bitrateForShareSettings } from '../store';
import { roleLabel } from '../permissions';
import type { Member, ProfileLink, ProfileVisibility, TopFriend, FriendUser } from '../types';
import { useVoice } from './voiceContext';
import { ConfirmDialog } from './ConfirmDialog';
import { toast } from './Toast';
import { pathToShareLink } from '../deeplink';
import { useAudioDevices, useVideoDevices, useStandaloneMicLevel } from '../hooks';
import { ImageUploader } from './ImageUploader';
import { uploadAvatar, uploadServerIcon, uploadProfileBanner, uploadProfileBackground } from '../imageUpload';

// =============================================================================
// CreateServerModal
// =============================================================================
export function CreateServerModal({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const server = await api.createServer({ name: name.trim() });
      setName('');
      onClose();
      onCreated(server.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create server');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Create a new server">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-text-muted">
          Servers are spaces for you and your friends. Each comes with a #general text channel and a voice room to start.
        </p>
        <Input
          label="Server name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="The Internet's Greatest Server"
          maxLength={64}
          autoFocus
          required
        />
        {error && <div className="rounded-xl border border-border-soft p-2 text-xs text-danger bg-danger/10">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
            {busy ? <Spinner size={14} /> : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// JoinServerModal
// =============================================================================
export function JoinServerModal({ open, onClose, onJoined }: {
  open: boolean;
  onClose: () => void;
  onJoined: (serverId: string) => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.joinServer(code.trim());
      setCode('');
      onClose();
      onJoined(result.serverId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to join');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Join with invite code">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-text-muted">
          Enter the invite code your friend shared with you.
        </p>
        <Input
          label="Invite code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. AB3XY7H2"
          maxLength={32}
          autoFocus
          required
          className="font-mono tracking-wider text-center"
        />
        {error && <div className="rounded-xl border border-border-soft p-2 text-xs text-danger bg-danger/10">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy || !code.trim()}>
            {busy ? <Spinner size={14} /> : 'Join'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// CreateChannelModal
// =============================================================================
export function CreateChannelModal({ open, onClose, serverId, onCreated }: {
  open: boolean;
  onClose: () => void;
  serverId: string;
  onCreated: (channelId: string) => void;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState<'text' | 'voice'>('text');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const ch = await api.createChannel(serverId, { name: name.trim(), type });
      setName('');
      onClose();
      onCreated(ch.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create channel');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Create a channel">
      <form onSubmit={submit} className="space-y-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setType('text')}
            className={`aim-button flex-1 ${type === 'text' ? 'aim-button-primary' : ''}`}
          >
            <span className="font-bold text-lg">#</span> Text
          </button>
          <button
            type="button"
            onClick={() => setType('voice')}
            className={`aim-button flex-1 ${type === 'voice' ? 'aim-button-primary' : ''}`}
          >
            <Icon name="speaker" size={14} /> Voice
          </button>
        </div>
        <Input
          label="Channel name"
          value={name}
          onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9_-]/g, ''))}
          placeholder={type === 'text' ? 'general' : 'lounge'}
          maxLength={64}
          autoFocus
          required
        />
        {error && <div className="rounded-xl border border-border-soft p-2 text-xs text-danger bg-danger/10">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
            {busy ? <Spinner size={14} /> : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// =============================================================================
// ManageMemberModal — change role / kick a member
// =============================================================================
export function ManageMemberModal({
  open,
  onClose,
  serverId,
  member,
  myRole,
}: {
  open: boolean;
  onClose: () => void;
  serverId: string;
  member: Member | null;
  myRole: string | null | undefined;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingKick, setConfirmingKick] = useState(false);
  const [confirmingTransfer, setConfirmingTransfer] = useState(false);

  if (!member) return null;

  const displayName = member.displayName || member.username;
  // Role options below my own rank
  const myRank = roleRank(myRole);
  const allRoles: Array<'admin' | 'moderator' | 'member'> = ['admin', 'moderator', 'member'];
  const assignable = allRoles.filter((r) => myRank > roleRank(r));

  async function changeRole(newRole: 'admin' | 'moderator' | 'member') {
    if (!member) return;
    setBusy(true);
    setError(null);
    try {
      await api.changeRole(serverId, member.userId, newRole);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to change role');
    } finally {
      setBusy(false);
    }
  }

  async function transfer() {
    if (!member) return;
    setConfirmingTransfer(false);
    setBusy(true);
    setError(null);
    try {
      await api.transferServer(serverId, member.userId);
      toast.success(`${displayName} now owns this server`);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to transfer ownership');
    } finally {
      setBusy(false);
    }
  }

  async function kick() {
    if (!member) return;
    setConfirmingKick(false);
    setBusy(true);
    setError(null);
    try {
      await api.kickMember(serverId, member.userId);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to kick member');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Manage ${displayName}`}>
      <div className="space-y-4">
        <div className="text-sm text-text-muted">
          Current role:{' '}
          <span className="font-semibold text-text">{roleLabel(member.role)}</span>
        </div>

        {assignable.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-2">
              Change role
            </div>
            <div className="grid grid-cols-3 gap-2">
              {assignable.map((r) => (
                <Button
                  key={r}
                  onClick={() => changeRole(r)}
                  disabled={busy || member.role === r}
                  variant={member.role === r ? 'default' : 'default'}
                  className={member.role === r ? 'opacity-50!' : ''}
                >
                  {roleLabel(r)}
                </Button>
              ))}
            </div>
            <div className="text-[10px] text-text-subtle mt-1">
              You can only assign roles below your own rank.
            </div>
          </div>
        )}

        <div className="border-t border-border-soft" />

        <div>
          <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-2">
            Danger zone
          </div>
          {myRole === 'owner' && (
            <Button onClick={() => setConfirmingTransfer(true)} disabled={busy} fullWidth className="mb-2">
              Transfer ownership
            </Button>
          )}
          <Button onClick={() => setConfirmingKick(true)} variant="danger" disabled={busy} fullWidth>
            <Icon name="logout" size={14} />
            Kick from server
          </Button>
        </div>

        {error && (
          <div className="rounded-xl border border-border-soft p-2 text-xs text-danger bg-danger/10">{error}</div>
        )}

        <div className="flex justify-end pt-1">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
      <ConfirmDialog
        open={confirmingTransfer}
        title="Transfer ownership"
        body={
          <>
            Make <b>{displayName}</b> the owner of this server? You'll become an admin, and only they can give
            ownership back.
          </>
        }
        confirmLabel="Transfer"
        destructive
        onConfirm={() => void transfer()}
        onCancel={() => setConfirmingTransfer(false)}
      />
      <ConfirmDialog
        open={confirmingKick}
        title="Kick member"
        body={
          <>
            Kick <b>{displayName}</b> from this server? They can rejoin with a new invite.
          </>
        }
        confirmLabel="Kick"
        destructive
        onConfirm={() => void kick()}
        onCancel={() => setConfirmingKick(false)}
      />
    </Modal>
  );
}

// Inline rank helper to avoid import cycle
function roleRank(role: string | null | undefined): number {
  switch (role) {
    case 'owner': return 4;
    case 'admin': return 3;
    case 'moderator': return 2;
    case 'member': return 1;
    default: return 0;
  }
}

// =============================================================================
// SettingsModal — edit display name + sounds toggle
// =============================================================================
export function SettingsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = useApp((s) => s.user);
  type Tab = 'appearance' | 'notifications' | 'audio' | 'screen' | 'account';
  const [tab, setTab] = useState<Tab>('account');

  // Reset to the first tab whenever the modal opens — feels less stale than
  // remembering whichever tab was last viewed days ago. (Profile customization
  // now lives on its own page at /settings/profile.)
  useEffect(() => {
    if (open) setTab('account');
  }, [open]);

  if (!me) return null;

  return (
    <Modal open={open} onClose={onClose} title="Settings" width="w-[640px]">
      <div className="flex flex-col sm:flex-row gap-3 -m-1">
        {/* Tab rail. Below `sm` it becomes a horizontally scrolling row above
            the content: as a side rail on a phone it took half the modal's
            width and squeezed every tab's content into a ~180px column. */}
        <nav
          aria-label="Settings sections"
          className="flex flex-row sm:flex-col gap-0.5 sm:w-32 shrink-0 overflow-x-auto sm:overflow-visible pb-2 sm:pb-0 border-b sm:border-b-0 border-border-soft"
        >
          <SettingsTab current={tab} value="appearance" onClick={setTab} icon="settings">Appearance</SettingsTab>
          <SettingsTab current={tab} value="notifications" onClick={setTab} icon="bell">Notifications</SettingsTab>
          <SettingsTab current={tab} value="audio" onClick={setTab} icon="mic">Audio &amp; Voice</SettingsTab>
          <SettingsTab current={tab} value="screen" onClick={setTab} icon="monitor">Screen &amp; Video</SettingsTab>
          <SettingsTab current={tab} value="account" onClick={setTab} icon="logout">Account</SettingsTab>
        </nav>

        {/* Tab content. Fixed height (560px, capped to viewport on small
            screens) keeps the modal the same size as you switch tabs —
            without it the modal would resize every time you click between
            a short tab (Notifications) and a tall one (Audio), which is
            disorienting. Each tab scrolls internally when its content
            exceeds the height. */}
        <div className="flex-1 min-w-0 sm:pl-3 sm:border-l border-border-soft">
          {/* 14rem (not 12): on phones the tab row now sits above this. */}
          <div className="overflow-y-auto" style={{ height: 'min(560px, calc(100dvh - 14rem))' }}>
            {tab === 'appearance' && <AppearanceTab />}
            {tab === 'notifications' && <NotificationsTab />}
            {tab === 'audio' && <AudioSettingsSection />}
            {tab === 'screen' && <ScreenSettingsTab />}
            {tab === 'account' && <AccountTab />}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function SettingsTab({
  current,
  value,
  onClick,
  icon,
  children,
}: {
  current: string;
  value: string;
  onClick: (v: never) => void;
  icon: string;
  children: ReactNode;
}) {
  const isActive = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value as never)}
      aria-current={isActive ? 'page' : undefined}
      className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left whitespace-nowrap shrink-0 transition-colors ${
        isActive
          ? 'bg-accent/20 text-text font-semibold'
          : 'text-text-muted hover:bg-elevated hover:text-text'
      }`}
    >
      <Icon name={icon} size={13} />
      {children}
    </button>
  );
}

// =============================================================================
// ProfileTab — avatar, screen name, display name
// =============================================================================
// Exported: rendered full-page by ProfileEditScreen (/settings/profile), no
// longer a tab in the Settings modal.
export function ProfileEditorBody() {
  const me = useApp((s) => s.user)!;
  const setUser = useApp((s) => s.setUser);
  const [displayName, setDisplayName] = useState(me.displayName ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDisplayName(me.displayName ?? '');
  }, [me.displayName]);

  async function saveName() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await api.updateProfile({ displayName: displayName.trim() || null });
      setUser(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <ImageUploader
        name={me.displayName || me.username}
        currentUrl={me.avatarUrl ?? null}
        size={72}
        hint="Square images work best — JPG/PNG/WebP up to 20 MB"
        onUpload={async (file) => {
          const url = await uploadAvatar(file);
          setUser({ ...me, avatarUrl: url });
        }}
        onRemove={async () => {
          await api.removeMyAvatar();
          setUser({ ...me, avatarUrl: null });
        }}
      />
      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">
          Screen name
        </label>
        <div className="aim-input bg-elevated/60 text-text-muted cursor-not-allowed">
          @{me.username}
        </div>
        <div className="text-[10px] text-text-subtle mt-0.5">
          Your unique handle. Can't be changed.
        </div>
      </div>
      <Input
        label="Display name"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        placeholder="What friends will see"
        maxLength={64}
      />
      <div className="flex items-center gap-2">
        <Button onClick={saveName} variant="primary" disabled={busy}>
          {busy ? <Spinner size={14} /> : 'Save'}
        </Button>
        {saved && <span className="text-xs text-online">✓ Saved</span>}
        {error && <span className="text-xs text-danger">{error}</span>}
      </div>

      <ProfilePageSection />
    </div>
  );
}

// =============================================================================
// ProfilePageSection — the customizable public profile page fields.
// =============================================================================
// Banner, About me, pronouns, links, accent color, and who-can-view. These
// aren't part of the lightweight `user` store object, so we fetch the current
// values once via the public-profile endpoint (the user viewing their own).
function ProfilePageSection() {
  const me = useApp((s) => s.user)!;
  const [loaded, setLoaded] = useState(false);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [cssText, setCssText] = useState('');
  const [cssDropped, setCssDropped] = useState<string[]>([]);
  const [cssBusy, setCssBusy] = useState(false);
  const [cssSaved, setCssSaved] = useState(false);
  const [song, setSong] = useState<{ provider: string | null; embedUrl: string; title: string | null } | null>(null);
  const [songInput, setSongInput] = useState('');
  const [songBusy, setSongBusy] = useState(false);
  const [songError, setSongError] = useState<string | null>(null);
  const [bio, setBio] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [customStatus, setCustomStatus] = useState('');
  const [accent, setAccent] = useState('');
  const [links, setLinks] = useState<ProfileLink[]>([]);
  const [visibility, setVisibility] = useState<ProfileVisibility>('public');
  const [topFriends, setTopFriends] = useState<TopFriend[]>([]);
  const [allFriends, setAllFriends] = useState<FriendUser[]>([]);
  const [topBusy, setTopBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.getUserProfile(me.username),
      api.getFriends().catch(() => null),
      api.getMyProfileCss().catch(() => ({ css: '' })),
    ])
      .then(([p, friends, css]) => {
        if (cancelled) return;
        setBannerUrl(p.bannerUrl);
        setBackgroundUrl(p.backgroundUrl);
        setBio(p.bio ?? '');
        setPronouns(p.pronouns ?? '');
        setCustomStatus(p.customStatus ?? '');
        setAccent(p.accent ?? '');
        setLinks(p.links);
        setVisibility(p.visibility);
        setTopFriends(p.topFriends);
        setCssText(css.css);
        setSong(p.song);
        setSongInput(p.song?.title ?? '');
        if (friends) setAllFriends(friends.friends);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [me.username]);

  // Top Friends apply immediately (their own endpoint + ordering) — gives
  // instant feedback without a separate save button. The PUT returns the
  // canonical (re-validated, online-stamped) list which we set back.
  async function applyTop(ids: string[]) {
    setTopBusy(true);
    try {
      const r = await api.setMyTopFriends(ids);
      setTopFriends(r.topFriends);
    } catch {
      /* leave current selection on failure */
    } finally {
      setTopBusy(false);
    }
  }
  const moveTop = (i: number, dir: -1 | 1) => {
    const ids = topFriends.map((f) => f.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    const tmp = ids[i]!;
    ids[i] = ids[j]!;
    ids[j] = tmp;
    void applyTop(ids);
  };

  async function saveCss() {
    setCssBusy(true);
    setCssSaved(false);
    try {
      const r = await api.setMyProfileCss(cssText);
      setCssDropped(r.dropped);
      setCssSaved(true);
      setTimeout(() => setCssSaved(false), 2000);
    } catch {
      /* ignore */
    } finally {
      setCssBusy(false);
    }
  }
  async function clearCss() {
    setCssBusy(true);
    try {
      await api.clearMyProfileCss();
      setCssText('');
      setCssDropped([]);
    } catch {
      /* ignore */
    } finally {
      setCssBusy(false);
    }
  }

  async function saveSong() {
    if (!songInput.trim()) return;
    setSongBusy(true);
    setSongError(null);
    try {
      const r = await api.setMySong(songInput.trim());
      setSong(r.song);
    } catch (err) {
      setSongError(err instanceof ApiError ? err.message : 'Could not add that link');
    } finally {
      setSongBusy(false);
    }
  }
  async function removeSong() {
    setSongBusy(true);
    try {
      await api.removeMySong();
      setSong(null);
      setSongInput('');
    } finally {
      setSongBusy(false);
    }
  }

  function setLink(i: number, patch: Partial<ProfileLink>) {
    setLinks((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      // Drop empty link rows; trim everything.
      const cleanLinks = links
        .map((l) => ({ label: l.label.trim(), url: l.url.trim() }))
        .filter((l) => l.label && l.url);
      await api.updateMyProfile({
        bio: bio.trim() || null,
        pronouns: pronouns.trim() || null,
        customStatus: customStatus.trim() || null,
        accent: accent || null,
        links: cleanLinks,
        visibility,
      });
      setLinks(cleanLinks);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) {
    return (
      <div className="border-t border-border-soft pt-3 mt-2 flex items-center gap-2 text-text-muted text-xs">
        <Spinner size={12} /> Loading profile…
      </div>
    );
  }

  const visOptions: { id: ProfileVisibility; label: string }[] = [
    { id: 'public', label: 'Anyone' },
    { id: 'friends_shared_server', label: 'Friends & servers' },
    { id: 'friends', label: 'Friends only' },
  ];

  return (
    <div className="border-t border-border-soft pt-3 mt-2 space-y-3">
      <div>
        <div className="text-sm text-text font-medium">Public profile page</div>
        <div className="text-xs text-text-muted">
          Customize the page people see at /u/{me.username}.
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">
          Banner
        </label>
        <ImageUploader
          name={me.displayName || me.username}
          currentUrl={bannerUrl}
          size={64}
          hint="Wide images work best — shown across the top of your profile"
          onUpload={async (file) => {
            const url = await uploadProfileBanner(file);
            setBannerUrl(url);
          }}
          onRemove={async () => {
            await api.removeMyBanner();
            setBannerUrl(null);
          }}
        />
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">
          Page background
        </label>
        <ImageUploader
          name={me.displayName || me.username}
          currentUrl={backgroundUrl}
          size={64}
          hint="Tiled or covering background behind your profile. Reference it in custom CSS as var(--profile-bg)."
          onUpload={async (file) => {
            const url = await uploadProfileBackground(file);
            setBackgroundUrl(url);
          }}
          onRemove={async () => {
            await api.removeMyBackground();
            setBackgroundUrl(null);
          }}
        />
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">
          About me
        </label>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={2000}
          rows={4}
          placeholder="Tell people about yourself…"
          className="aim-input w-full resize-y min-h-[80px]"
        />
      </div>

      <Input
        label="Pronouns"
        value={pronouns}
        onChange={(e) => setPronouns(e.target.value)}
        placeholder="e.g. she/her"
        maxLength={40}
      />

      <Input
        label="Status"
        value={customStatus}
        onChange={(e) => setCustomStatus(e.target.value)}
        placeholder="What are you up to?"
        maxLength={128}
      />

      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">
          Accent color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={accent || '#888888'}
            onChange={(e) => setAccent(e.target.value)}
            className="w-9 h-9 rounded-sm border border-border-soft bg-surface cursor-pointer"
            aria-label="Accent color"
          />
          {accent && (
            <button
              type="button"
              onClick={() => setAccent('')}
              className="text-xs text-text-subtle hover:text-text"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">
          Links
        </label>
        <div className="space-y-1.5">
          {links.map((l, i) => (
            // Grid (not flex): explicit tracks let each .aim-input fill its
            // cell cleanly — `.aim-input` is width:100%, which fights flex-basis
            // sizing and squished the URL field.
            <div key={i} className="grid grid-cols-[7rem_1fr_auto] gap-1.5 items-center">
              <input
                value={l.label}
                onChange={(e) => setLink(i, { label: e.target.value })}
                placeholder="Label"
                maxLength={40}
                className="aim-input min-w-0"
              />
              <input
                value={l.url}
                onChange={(e) => setLink(i, { url: e.target.value })}
                placeholder="https://…"
                maxLength={300}
                className="aim-input min-w-0"
              />
              <button
                type="button"
                onClick={() => setLinks((ls) => ls.filter((_, idx) => idx !== i))}
                className="text-text-subtle hover:text-danger px-1"
                aria-label="Remove link"
              >
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
          {links.length < 8 && (
            <button
              type="button"
              onClick={() => setLinks((ls) => [...ls, { label: '', url: '' }])}
              className="text-xs text-accent hover:underline flex items-center gap-1"
            >
              <Icon name="plus" size={11} /> Add link
            </button>
          )}
        </div>
      </div>

      <div>
        <div className="text-sm text-text font-medium">Who can view my profile</div>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {visOptions.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => setVisibility(o.id)}
              className={`text-xs px-2.5 py-1.5 rounded-aim border transition-colors ${
                visibility === o.id
                  ? 'bg-accent text-accent-text border-accent font-semibold'
                  : 'bg-surface text-text-muted border-border-soft hover:bg-elevated'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-sm text-text font-medium">
          Top Friends{' '}
          <span className="text-text-subtle font-normal">({topFriends.length}/12)</span>
        </div>
        <div className="text-xs text-text-muted mb-1.5">Pin friends to your profile. Changes save instantly.</div>
        {topFriends.length > 0 && (
          <div className="space-y-1 mb-2">
            {topFriends.map((f, i) => (
              <div key={f.id} className="flex items-center gap-2 bg-elevated/50 rounded-sm px-2 py-1">
                <Avatar name={f.displayName || f.username} url={f.avatarUrl} size={22} />
                <span className="text-xs text-text truncate flex-1">{f.displayName || f.username}</span>
                <button type="button" disabled={topBusy || i === 0} onClick={() => moveTop(i, -1)}
                  className="text-text-subtle hover:text-text disabled:opacity-30 px-0.5" aria-label="Move up">
                  <Icon name="chevron-up" size={13} />
                </button>
                <button type="button" disabled={topBusy || i === topFriends.length - 1} onClick={() => moveTop(i, 1)}
                  className="text-text-subtle hover:text-text disabled:opacity-30 px-0.5" aria-label="Move down">
                  <Icon name="chevron-down" size={13} />
                </button>
                <button type="button" disabled={topBusy}
                  onClick={() => applyTop(topFriends.filter((x) => x.id !== f.id).map((x) => x.id))}
                  className="text-text-subtle hover:text-danger px-0.5" aria-label="Remove">
                  <Icon name="trash" size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        {topFriends.length < 12 && (
          <div className="flex flex-wrap gap-1.5">
            {allFriends
              .filter((f) => !topFriends.some((t) => t.id === f.id))
              .map((f) => (
                <button key={f.id} type="button" disabled={topBusy}
                  onClick={() => applyTop([...topFriends.map((x) => x.id), f.id])}
                  className="text-xs px-2 py-1 rounded-aim border border-border-soft bg-surface text-text-muted hover:bg-elevated disabled:opacity-50 flex items-center gap-1">
                  <Icon name="plus" size={10} /> {f.displayName || f.username}
                </button>
              ))}
            {allFriends.length === 0 && (
              <span className="text-xs text-text-subtle">Add some friends first to pin them here.</span>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-border-soft pt-3">
        <div className="text-sm text-text font-medium">Profile song</div>
        <div className="text-xs text-text-muted mb-1.5">
          Paste a <strong>Spotify</strong>, <strong>SoundCloud</strong>, <strong>Apple Music</strong>, or{' '}
          <strong>Bandcamp</strong> track/album link — it embeds the player on your profile.
        </div>
        <div className="flex gap-2">
          <input
            value={songInput}
            onChange={(e) => setSongInput(e.target.value)}
            placeholder="https://open.spotify.com/track/…"
            className="aim-input flex-1 min-w-0"
          />
          <Button onClick={saveSong} disabled={songBusy || !songInput.trim()}>
            {songBusy ? <Spinner size={12} /> : song ? 'Update' : 'Add'}
          </Button>
          {song && (
            <Button onClick={removeSong} disabled={songBusy} variant="danger">
              Remove
            </Button>
          )}
        </div>
        {song && (
          <div className="text-[11px] text-text-subtle mt-1.5">
            ♪ Linked{song.title ? `: ${song.title}` : ''}
            {song.provider ? ` (${song.provider.replace('_', ' ')})` : ''}
          </div>
        )}
        {songError && <div className="text-xs text-danger mt-1">{songError}</div>}
      </div>

      <div className="border-t border-border-soft pt-3">
        <div className="text-sm text-text font-medium">Custom CSS</div>
        <div className="text-xs text-text-muted mb-1.5">
          Style your page (selectors are scoped to your profile). External images, @import,
          and scripts are stripped for safety — use var(--profile-bg) for your background.
        </div>
        <textarea
          value={cssText}
          onChange={(e) => setCssText(e.target.value)}
          rows={6}
          spellCheck={false}
          placeholder={'.about { font-family: "Comic Sans MS"; }\nh1 { color: var(--profile-accent); }'}
          className="aim-input w-full font-mono text-xs resize-y min-h-[120px]"
        />
        <div className="flex items-center gap-2 mt-1.5">
          <Button onClick={saveCss} disabled={cssBusy}>
            {cssBusy ? <Spinner size={12} /> : 'Apply CSS'}
          </Button>
          {cssText && (
            <Button onClick={clearCss} disabled={cssBusy} variant="danger">
              Clear
            </Button>
          )}
          {cssSaved && <span className="text-xs text-online">✓ Applied</span>}
        </div>
        {cssDropped.length > 0 && (
          <div className="text-[11px] text-text-subtle mt-1.5 bg-elevated/50 rounded-sm px-2 py-1.5">
            <span className="text-danger font-semibold">Removed for safety:</span>{' '}
            {cssDropped.slice(0, 12).join(', ')}
            {cssDropped.length > 12 && ` +${cssDropped.length - 12} more`}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={save} variant="primary" disabled={busy}>
          {busy ? <Spinner size={14} /> : 'Save profile'}
        </Button>
        {saved && <span className="text-xs text-online">✓ Saved</span>}
        {error && <span className="text-xs text-danger">{error}</span>}
      </div>
    </div>
  );
}

// =============================================================================
// InvisibilityToggle — "appear offline to others"
// =============================================================================
function InvisibilityToggle() {
  const me = useApp((s) => s.user)!;
  const setUser = useApp((s) => s.setUser);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isInvisible = me.isInvisible ?? false;

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      const next = !isInvisible;
      const updated = await api.updateProfile({ isInvisible: next });
      setUser(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update');
    } finally {
      setBusy(false);
    }
  }

  return (
    // first:* — it opens the Account tab, where a top divider has nothing above it to divide.
    <div className="border-t border-border-soft pt-3 mt-2 space-y-2 first:border-t-0 first:pt-0 first:mt-0">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-text font-medium">Appear offline</div>
          <div className="text-xs text-text-muted">
            Others see you as offline. You still receive messages and can use
            voice normally — only your visibility changes.
          </div>
        </div>
        <Button onClick={toggle} disabled={busy} variant={isInvisible ? 'primary' : undefined}>
          {busy ? <Spinner size={12} /> : isInvisible ? 'On' : 'Off'}
        </Button>
      </div>
      {error && <div className="text-xs text-danger">{error}</div>}
    </div>
  );
}

// =============================================================================
// DmPrivacySetting — who can start a DM / call you
// =============================================================================
function DmPrivacySetting() {
  const me = useApp((s) => s.user)!;
  const setUser = useApp((s) => s.setUser);
  const [busy, setBusy] = useState(false);
  const current = me.dmPrivacy ?? 'everyone';

  const options: { id: 'everyone' | 'friends' | 'mutual_servers'; label: string }[] = [
    { id: 'everyone', label: 'Everyone' },
    { id: 'mutual_servers', label: 'Members of my servers' },
    { id: 'friends', label: 'Friends only' },
  ];

  async function pick(value: 'everyone' | 'friends' | 'mutual_servers') {
    if (value === current || busy) return;
    setBusy(true);
    try {
      const updated = await api.updateProfile({ dmPrivacy: value });
      setUser(updated);
    } catch {
      /* keep current selection on failure */
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-t border-border-soft pt-3 mt-2 space-y-2">
      <div className="min-w-0">
        <div className="text-sm text-text font-medium">Who can message or call me</div>
        <div className="text-xs text-text-muted">
          Applies when someone starts a new DM or call. Friends can always reach you.
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            disabled={busy}
            onClick={() => pick(o.id)}
            className={`text-xs px-2.5 py-1.5 rounded-aim border transition-colors disabled:opacity-50 ${
              current === o.id
                ? 'bg-accent text-accent-text border-accent font-semibold'
                : 'bg-surface text-text-muted border-border-soft hover:bg-elevated'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// AppearanceTab — theme
// =============================================================================
function AppearanceTab() {
  const theme = useApp((s) => s.theme);
  const setTheme = useApp((s) => s.setTheme);
  return (
    <div>
      <div className="text-sm text-text font-medium mb-1">Theme</div>
      <div className="text-xs text-text-muted mb-3">
        Pick a look. Changes apply instantly.
      </div>
      <div className="grid grid-cols-2 gap-2">
        {THEMES.map((t) => (
          <ThemeSwatch
            key={t.id}
            theme={t}
            selected={theme === t.id}
            onSelect={() => setTheme(t.id)}
          />
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// ThemeSwatch — preview tile + radio behavior for picker
// =============================================================================
// Renders a small preview using the theme's CSS variables in inline style
// (rather than relying on data-theme being set on this subtree). That lets
// every swatch show its own colors regardless of which theme is currently
// active.
// =============================================================================
function ThemeSwatch({
  theme,
  selected,
  onSelect,
}: {
  theme: typeof THEMES[number];
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative rounded-xl border-2 transition-all text-left overflow-hidden ${
        selected
          ? 'border-accent ring-2 ring-accent/40'
          : 'border-border-soft hover:border-border'
      }`}
      title={theme.desc}
      aria-pressed={selected}
    >
      {/* Preview canvas — rendered in the swatch's OWN colors via data-theme.
          We set data-theme on this wrapper so the [data-theme="..."] CSS
          selectors apply to the subtree below, regardless of which theme is
          active globally. The result: every swatch shows its actual look. */}
      <div data-theme={theme.id} className="bg-(--c-bg) p-2.5">
        {/* Faux chat row */}
        <div className="bg-(--c-surface) border border-(--c-border-soft) rounded-sm p-2 mb-2 shadow-xs">
          <div className="flex items-center gap-1.5 mb-1.5">
            <div className="w-2 h-2 rounded-full bg-(--c-online) shrink-0" />
            <div className="h-2 rounded-sm bg-(--c-text) opacity-90" style={{ width: '40%' }} />
            <div className="h-1.5 rounded-sm bg-(--c-text-muted) opacity-60 ml-auto" style={{ width: '15%' }} />
          </div>
          <div className="h-1.5 rounded-sm bg-(--c-text-muted) opacity-60 mb-1" style={{ width: '90%' }} />
          <div className="h-1.5 rounded-sm bg-(--c-text-muted) opacity-60" style={{ width: '60%' }} />
        </div>
        {/* Color rail — three swatches showing accent / link / online */}
        <div className="flex gap-1.5">
          <div className="flex-1 h-4 rounded-sm bg-(--c-accent)" title="Accent" />
          <div className="flex-1 h-4 rounded-sm bg-(--c-link)" title="Links" />
          <div className="flex-1 h-4 rounded-sm bg-(--c-online)" title="Online" />
        </div>
      </div>
      {/* Label strip — uses outer (active) theme so it reads naturally */}
      <div className="px-2 py-1.5 bg-surface border-t border-border-soft">
        <div className="text-xs font-semibold text-text">{theme.label}</div>
        <div className="text-[10px] text-text-muted truncate">{theme.desc}</div>
      </div>
      {selected && (
        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-accent text-accent-text flex items-center justify-center text-[11px] font-bold shadow-sm">
          ✓
        </div>
      )}
    </button>
  );
}

// =============================================================================
// NotificationsTab — sound + screen-share preferences
// =============================================================================
function NotificationsTab() {
  const soundsEnabled = useApp((s) => s.soundsEnabled);
  const setSoundsEnabled = useApp((s) => s.setSoundsEnabled);

  const [pushState, setPushState] = useState<PushState>('unsubscribed');
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => {
    getPushState().then(setPushState).catch(() => {});
  }, []);
  async function togglePush() {
    setPushBusy(true);
    try {
      if (pushState === 'subscribed') await unsubscribePush();
      else await subscribePush();
      setPushState(await getPushState());
    } finally {
      setPushBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0 pr-3">
          <div className="text-sm text-text font-medium">Desktop notifications</div>
          <div className="text-xs text-text-muted">
            {pushState === 'unsupported'
              ? 'Not supported in this browser.'
              : pushState === 'denied'
                ? 'Blocked — allow notifications for this site in your browser settings.'
                : 'Get notified of @mentions and DMs, even when the tab is closed.'}
          </div>
        </div>
        <Button
          onClick={togglePush}
          disabled={pushBusy || pushState === 'unsupported' || pushState === 'denied'}
        >
          <Icon name={pushState === 'subscribed' ? 'bell' : 'bell-off'} size={14} />
          {pushState === 'subscribed' ? 'On' : 'Off'}
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0 pr-3">
          <div className="text-sm text-text font-medium">Message sounds</div>
          <div className="text-xs text-text-muted">
            Play AIM-style chimes for new messages
          </div>
        </div>
        <Button onClick={() => setSoundsEnabled(!soundsEnabled)}>
          <Icon name={soundsEnabled ? 'bell' : 'bell-off'} size={14} />
          {soundsEnabled ? 'On' : 'Off'}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// AccountTab — change password (more to come: email, deletion, etc.)
// =============================================================================
function AccountTab() {
  const [sessionsKey, setSessionsKey] = useState(0);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  function reset() {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    setBusy(true);
    try {
      const res = await api.changePassword({ currentPassword, newPassword });
      // The password change revoked this device's old token; swap to the fresh
      // one the server issued so subsequent requests stay authenticated.
      if (res.token) setToken(res.token);
      setSessionsKey((k) => k + 1);
      setSuccess(true);
      reset();
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to change password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <InvisibilityToggle />
      <DmPrivacySetting />
      <div className="text-sm text-text font-medium mb-1 border-t border-border-soft pt-3 mt-3">Change password</div>
      <div className="text-xs text-text-muted mb-3">
        Updating your password also signs out your other devices.
      </div>
      <form onSubmit={submit} className="space-y-3">
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          required
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          minLength={8}
          required
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        <div className="flex items-center gap-2 pt-1">
          <Button type="submit" variant="primary" disabled={busy || !currentPassword || !newPassword || !confirmPassword}>
            {busy ? <Spinner size={14} /> : 'Update password'}
          </Button>
          {success && <span className="text-xs text-online">✓ Password updated</span>}
          {error && <span className="text-xs text-danger">{error}</span>}
        </div>
      </form>
      <TwoFactorSettings />
      {/* Remount after a password change: it signed out the other sessions. */}
      <ActiveSessions key={sessionsKey} />
      <AccountDataSettings />
    </div>
  );
}

// =============================================================================
// AudioSettingsSection — voice chat audio quality / noise gate
// =============================================================================
// =============================================================================
// AudioDeviceSection — pick mic + speaker
// =============================================================================
// Two dropdowns (mic, output) plus a small "Test speaker" button. We don't
// have a "test mic" button because the existing live mic level meter
// further down the page already serves that purpose.
//
// Permission gate: browsers don't reveal device LABELS until the page has
// been granted mic permission. When labels are missing, we surface a
// "Show device names" button that does a one-time getUserMedia() to grant
// permission, then re-enumerates so labels populate.
// =============================================================================
function AudioDeviceSection() {
  const { inputs, outputs, hasLabels, outputSupported, requestLabels } = useAudioDevices();
  const audioDevices = useApp((s) => s.audioDevices);
  const setAudioDevices = useApp((s) => s.setAudioDevices);
  const [requesting, setRequesting] = useState(false);

  async function onRequestLabels() {
    setRequesting(true);
    try {
      await requestLabels();
    } finally {
      setRequesting(false);
    }
  }

  // Tiny in-memory test tone for the speaker test. We can't use a real
  // audio file because we don't ship one — generate a soft sine-wave
  // pluck via WebAudio. Brief and pleasant, not a startle.
  async function testSpeaker() {
    try {
      const ctx = new AudioContext();
      // Route through a destination element so we can pin to the user's
      // selected output. AudioContext.destination always goes to the
      // system default — to honor the user's chosen output, we must
      // create a MediaStreamDestination, attach to an <audio>, and call
      // setSinkId on that element.
      const dst = ctx.createMediaStreamDestination();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440;
      gain.gain.value = 0;
      osc.connect(gain).connect(dst);

      const audio = new Audio();
      audio.srcObject = dst.stream;
      // Pin to selected output if supported + selected.
      const id = audioDevices.outputDeviceId;
      if (id && outputSupported && 'setSinkId' in audio) {
        try {
          await audio.setSinkId(id);
        } catch {
          /* fall through to default output */
        }
      }
      await audio.play().catch(() => {});

      // Envelope: short attack, gentle decay. ~700ms total.
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.18, now + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
      osc.start(now);
      osc.stop(now + 0.75);

      // Clean up the AudioContext and audio element after playback.
      setTimeout(() => {
        audio.pause();
        audio.srcObject = null;
        ctx.close().catch(() => {});
      }, 900);
    } catch (err) {
      console.warn('test speaker failed:', err);
    }
  }

  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-2">
        Audio devices
      </div>
      {!hasLabels && (
        <div className="rounded-xl border border-border-soft bg-elevated px-2 py-1.5 mb-2 flex items-center gap-2">
          <div className="text-xs text-text-muted flex-1 leading-snug">
            Allow mic access to see device names. We don't record anything;
            this just unlocks the labels.
          </div>
          <Button onClick={onRequestLabels} disabled={requesting}>
            {requesting ? <Spinner size={12} /> : 'Show names'}
          </Button>
        </div>
      )}
      <div className="space-y-2">
        <DeviceRow
          label="Microphone"
          devices={inputs}
          selectedId={audioDevices.micDeviceId}
          onChange={(id) => setAudioDevices({ micDeviceId: id })}
        />
        {outputSupported ? (
          <DeviceRow
            label="Speaker / output"
            devices={outputs}
            selectedId={audioDevices.outputDeviceId}
            onChange={(id) => setAudioDevices({ outputDeviceId: id })}
            extra={
              <Button onClick={testSpeaker} title="Play a short tone">
                Test
              </Button>
            }
          />
        ) : (
          <div className="text-[11px] text-text-subtle italic">
            Output device selection isn't supported in this browser.
          </div>
        )}
      </div>
      <div className="text-[10px] text-text-subtle italic mt-2 leading-snug">
        Mic changes apply on your next voice connect (or instantly while
        connected). Output applies immediately.
      </div>
    </div>
  );
}

// CameraDeviceSection — pick which webcam the Camera button uses.
function CameraDeviceSection() {
  const { cameras, hasLabels, requestLabels } = useVideoDevices();
  const cameraDeviceId = useApp((s) => s.audioDevices.cameraDeviceId);
  const setAudioDevices = useApp((s) => s.setAudioDevices);
  const [requesting, setRequesting] = useState(false);

  async function onRequestLabels() {
    setRequesting(true);
    try {
      await requestLabels();
    } finally {
      setRequesting(false);
    }
  }

  return (
    <div className="mt-5 pt-4 border-t border-border-soft">
      <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-2">
        Camera
      </div>
      {!hasLabels && cameras.length > 0 && (
        <div className="rounded-xl border border-border-soft bg-elevated px-2 py-1.5 mb-2 flex items-center gap-2">
          <div className="text-xs text-text-muted flex-1 leading-snug">
            Allow camera access to see device names.
          </div>
          <Button onClick={onRequestLabels} disabled={requesting}>
            {requesting ? <Spinner size={12} /> : 'Show names'}
          </Button>
        </div>
      )}
      {cameras.length === 0 ? (
        <div className="text-[11px] text-text-subtle italic">No cameras detected.</div>
      ) : (
        <DeviceRow
          label="Camera"
          devices={cameras}
          selectedId={cameraDeviceId}
          onChange={(id) => setAudioDevices({ cameraDeviceId: id })}
        />
      )}
      <div className="text-[10px] text-text-subtle italic mt-2 leading-snug">
        Switches instantly if your camera is on, otherwise applies when you turn
        it on.
      </div>
    </div>
  );
}

function DeviceRow({
  label,
  devices,
  selectedId,
  onChange,
  extra,
}: {
  label: string;
  devices: { deviceId: string; label: string; isDefault: boolean }[];
  selectedId: string | null;
  onChange: (id: string | null) => void;
  extra?: ReactNode;
}) {
  // Defensive: if the saved id isn't in the live device list (unplugged
  // headset), fall through to "system default" rather than showing a
  // confusing dangling selection.
  const known = devices.some((d) => d.deviceId === selectedId);
  const value = selectedId && known ? selectedId : '';
  return (
    <div>
      <label className="block text-xs text-text-muted mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value || null)}
          className="aim-input flex-1"
        >
          <option value="">System default</option>
          {devices.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label}
              {d.isDefault ? ' (system default)' : ''}
            </option>
          ))}
        </select>
        {extra}
      </div>
      {selectedId && !known && (
        <div className="text-[10px] text-text-subtle italic mt-0.5">
          Saved device isn't available — using system default.
        </div>
      )}
    </div>
  );
}

function AudioSettingsSection() {
  const audioSettings = useApp((s) => s.audioSettings);
  const setAudioSettings = useApp((s) => s.setAudioSettings);
  const mutedSpeechHint = useApp((s) => s.mutedSpeechHint);
  const setMutedSpeechHint = useApp((s) => s.setMutedSpeechHint);
  const audioDevices = useApp((s) => s.audioDevices);
  const inVoice = useVoice().inRoom;
  const onMicLevel = useVoice().onMicLevel;
  const [level, setLevel] = useState(-100);
  const [gateOpen, setGateOpen] = useState(false);

  // Threshold slider — local state for smooth dragging, debounce-write to store.
  // Declared before the standalone hook so the hook gets the current value.
  const [thresholdLocal, setThresholdLocal] = useState(audioSettings.noiseGateThresholdDb);
  useEffect(() => {
    setThresholdLocal(audioSettings.noiseGateThresholdDb);
  }, [audioSettings.noiseGateThresholdDb]);

  // Subscribe to mic level for the meter when in voice (uses the live
  // NoiseGateProcessor's level callback — no extra mic acquisition).
  useEffect(() => {
    if (!onMicLevel) return;
    return onMicLevel((db, open) => {
      setLevel(db);
      setGateOpen(open);
    });
  }, [onMicLevel]);

  // Standalone mic preview for when the user isn't in voice yet. Now
  // computes BOTH the display level AND the gate state itself, against
  // the live threshold — so the meter and gate indicator stay in sync
  // and accurately reflect what publishing would do. Mic isn't acquired
  // automatically — user clicks "Test mic" so we don't surprise them
  // with a recording indicator every time they open Settings.
  const standaloneMic = useStandaloneMicLevel({
    deviceId: audioDevices.micDeviceId,
    thresholdDb: thresholdLocal,
  });
  // When NOT in voice, mirror the standalone hook's outputs into the
  // shared `level` and `gateOpen` state the meter reads.
  useEffect(() => {
    if (inVoice) return;
    setLevel(standaloneMic.level);
    setGateOpen(standaloneMic.gateOpen);
  }, [inVoice, standaloneMic.level, standaloneMic.gateOpen]);

  return (
    <div>
      <AudioDeviceSection />

      <CameraDeviceSection />

      <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-2 mt-5 pt-4 border-t border-border-soft">
        Audio (voice chat)
      </div>
      <div className="space-y-2">
        <ToggleRow
          label="Warn when talking while muted"
          hint="Reminds you to unmute if your mic hears you speaking while muted"
          value={mutedSpeechHint}
          onChange={setMutedSpeechHint}
        />
        <ToggleRow
          label="Echo cancellation"
          hint="Reduces echo from your speakers picked up by the mic"
          value={audioSettings.echoCancellation}
          onChange={(v) => setAudioSettings({ echoCancellation: v })}
        />
        <ToggleRow
          label="Noise suppression"
          hint="Browser's built-in background noise filter (recommended)"
          value={audioSettings.noiseSuppression}
          onChange={(v) => setAudioSettings({ noiseSuppression: v })}
        />
        <ToggleRow
          label="Auto gain control"
          hint="Auto-adjusts mic volume to keep voice consistent"
          value={audioSettings.autoGainControl}
          onChange={(v) => setAudioSettings({ autoGainControl: v })}
        />
        <ToggleRow
          label="Noise gate"
          hint="Mutes mic during silences below the threshold"
          value={audioSettings.noiseGateEnabled}
          onChange={(v) => setAudioSettings({ noiseGateEnabled: v })}
        />

        {audioSettings.noiseGateEnabled && (
          <div className="rounded-xl border border-border-soft p-3 bg-elevated space-y-2">
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-text font-medium">Threshold</span>
              <span className="text-[10px] text-text-subtle font-mono">
                {Math.round(thresholdLocal)} dB
              </span>
            </div>
            <input
              type="range"
              min={-80}
              max={-20}
              step={1}
              value={thresholdLocal}
              onChange={(e) => {
                const v = Number(e.target.value);
                setThresholdLocal(v);
                setAudioSettings({ noiseGateThresholdDb: v });
              }}
              className="w-full"
            />
            <div className="text-[10px] text-text-subtle">
              Lower values let quieter sounds through. -50 dB is a good start.
            </div>

            {/* Live meter — works both in-voice (using the live processor's
                callback) and out-of-voice (using a standalone mic stream
                started on demand via "Test mic" below). */}
            {(inVoice || standaloneMic.active) && (
              <LevelMeter level={level} threshold={thresholdLocal} gateOpen={gateOpen} />
            )}
            {!inVoice && !standaloneMic.active && (
              <div className="flex items-center justify-between gap-2 pt-1">
                <div className="text-[10px] text-text-subtle">
                  Click to test the gate against your live mic.
                </div>
                <Button onClick={() => { void standaloneMic.request(); }}>
                  Test mic
                </Button>
              </div>
            )}
            {!inVoice && standaloneMic.active && (
              <div className="flex justify-end pt-1">
                <Button onClick={standaloneMic.stop}>Stop test</Button>
              </div>
            )}
            {standaloneMic.error && (
              <div className="text-[10px] text-danger">{standaloneMic.error}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// ScreenSettingsTab — Screen & Video tab in Settings.
// =============================================================================
// Currently houses screen share quality. Webcam controls will live here
// too once we add that feature — the tab is named broadly so the addition
// won't require a UI restructure.
// =============================================================================
function ScreenSettingsTab() {
  return (
    <div className="space-y-5">
      <ScreenShareSettingsSection />
      <CameraSettingsSection />
    </div>
  );
}

// =============================================================================
// CameraSettingsSection — quality preset for your outgoing webcam.
// =============================================================================
// Like screen share quality, this only affects what YOU publish. Cameras are
// auto-subscribed by everyone in the room (no watch-gate), so the presets cap
// lower than screen share — at typical tile sizes 1080p mostly burns upload
// bandwidth on your end and download on everyone else's.
// =============================================================================
function CameraSettingsSection() {
  const settings = useApp((s) => s.cameraSettings);
  const setSettings = useApp((s) => s.setCameraSettings);

  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-2">
        Webcam quality
      </div>
      <div className="space-y-3">
        <div>
          <div className="text-sm text-text font-medium mb-1.5">Resolution</div>
          <div className="flex gap-2">
            <Button
              onClick={() => setSettings({ resolution: 360 })}
              variant={settings.resolution === 360 ? 'primary' : undefined}
            >
              360p
            </Button>
            <Button
              onClick={() => setSettings({ resolution: 720 })}
              variant={settings.resolution === 720 ? 'primary' : undefined}
            >
              720p
            </Button>
            <Button
              onClick={() => setSettings({ resolution: 1080 })}
              variant={settings.resolution === 1080 ? 'primary' : undefined}
            >
              1080p
            </Button>
          </div>
        </div>
        <div>
          <div className="text-sm text-text font-medium mb-1.5">Framerate</div>
          <div className="flex gap-2">
            <Button
              onClick={() => setSettings({ framerate: 15 })}
              variant={settings.framerate === 15 ? 'primary' : undefined}
            >
              15 fps
            </Button>
            <Button
              onClick={() => setSettings({ framerate: 30 })}
              variant={settings.framerate === 30 ? 'primary' : undefined}
            >
              30 fps
            </Button>
          </div>
        </div>
        <div className="text-[11px] text-text-muted leading-snug">
          Applies the next time you turn your camera on — toggle it off and on
          to apply mid-call. Everyone in the room receives your camera, so
          higher settings cost bandwidth for the whole call.
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// ScreenShareSettingsSection — quality preset for outgoing screen shares.
// =============================================================================
// Affects only YOUR shares (the publisher chooses the quality); other users
// always receive whatever quality each sharer is publishing. Higher
// resolution/framerate uses more upload bandwidth and CPU.
// =============================================================================
function ScreenShareSettingsSection() {
  const settings = useApp((s) => s.screenShareSettings);
  const setSettings = useApp((s) => s.setScreenShareSettings);
  const bitrateMbps = (bitrateForShareSettings(settings) / 1_000_000).toFixed(1);

  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-2">
        Screen share quality
      </div>
      <div className="space-y-3">
        <div>
          <div className="text-sm text-text font-medium mb-1.5">Resolution</div>
          <div className="flex gap-2">
            <Button
              onClick={() => setSettings({ resolution: 720 })}
              variant={settings.resolution === 720 ? 'primary' : undefined}
            >
              720p
            </Button>
            <Button
              onClick={() => setSettings({ resolution: 1080 })}
              variant={settings.resolution === 1080 ? 'primary' : undefined}
            >
              1080p
            </Button>
          </div>
        </div>
        <div>
          <div className="text-sm text-text font-medium mb-1.5">Framerate</div>
          <div className="flex gap-2">
            <Button
              onClick={() => setSettings({ framerate: 30 })}
              variant={settings.framerate === 30 ? 'primary' : undefined}
            >
              30 fps
            </Button>
            <Button
              onClick={() => setSettings({ framerate: 60 })}
              variant={settings.framerate === 60 ? 'primary' : undefined}
            >
              60 fps
            </Button>
          </div>
        </div>
        <ToggleRow
          label="Share computer audio"
          hint={
            window.electronAPI?.isDesktop
              ? 'Include app/system sound with your screen share.'
              : 'Include your computer’s sound. Heads up: sharing a whole screen or window echoes the call back to others — share a single browser tab for clean audio.'
          }
          value={settings.shareAudio}
          onChange={(v) => setSettings({ shareAudio: v })}
        />
        <div className="text-[11px] text-text-muted leading-snug">
          Approx. {bitrateMbps} Mbps upload while sharing. Changes take effect
          on your next share — stop and restart your current share to apply.
          {settings.framerate === 60 && settings.resolution === 1080 && (
            <span className="block mt-1 text-text-subtle">
              1080p60 needs a fast upload (≥10 Mbps recommended) and uses more
              CPU on both ends.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-text font-medium">{label}</div>
        <div className="text-xs text-text-muted">{hint}</div>
      </div>
      <Button onClick={() => onChange(!value)}>{value ? 'On' : 'Off'}</Button>
    </div>
  );
}

function LevelMeter({
  level,
  threshold,
  gateOpen,
}: {
  level: number;       // dB, typically -100 to 0
  threshold: number;   // dB
  gateOpen: boolean;
}) {
  // Map -80..0 dB to 0..100%
  const pct = Math.max(0, Math.min(100, ((level + 80) / 80) * 100));
  const thresholdPct = Math.max(0, Math.min(100, ((threshold + 80) / 80) * 100));

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] text-text-subtle">Mic level</span>
        <span className={`text-[10px] font-mono ${gateOpen ? 'text-online' : 'text-text-subtle'}`}>
          {gateOpen ? 'open' : 'gated'}
        </span>
      </div>
      <div className="relative h-3 rounded-xl border border-border-soft bg-bg overflow-hidden">
        {/* Filled bar */}
        <div
          className={`absolute inset-y-0 left-0 transition-all ${
            gateOpen ? 'bg-online' : 'bg-text-subtle'
          }`}
          style={{ width: `${pct}%` }}
        />
        {/* Threshold marker */}
        <div
          className="absolute inset-y-0 w-px bg-danger"
          style={{ left: `${thresholdPct}%` }}
          title="Threshold"
        />
      </div>
    </div>
  );
}

// =============================================================================
// =============================================================================
// InviteCodeModal — show invite code with copy + rotate
// =============================================================================
// Rotation generates a new code on the server, immediately invalidating the
// old one. We update the displayed code in place so the user sees the new
// one without closing the modal. Visible only to owners/admins via the
// `canRotate` prop.
// =============================================================================
export function InviteCodeModal({ open, onClose, serverId, code, serverName, canRotate }: {
  open: boolean;
  onClose: () => void;
  serverId: string;
  code: string;
  serverName: string;
  canRotate: boolean;
}) {
  const [copied, setCopied] = useState(false);
  // Local copy of the code we display. Initialized from the prop; updated
  // when rotation succeeds. We don't sync back to the prop on close — the
  // parent's server list refresh will pick up the new code naturally.
  const [displayCode, setDisplayCode] = useState(code);
  const [rotating, setRotating] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);

  // If the parent's `code` prop changes (server list refreshed elsewhere),
  // pick that up — but only when the modal is closed, to avoid clobbering
  // a freshly-rotated code mid-view.
  useEffect(() => {
    if (!open) setDisplayCode(code);
  }, [code, open]);

  // Reset confirm/error state on each open.
  useEffect(() => {
    if (open) {
      setConfirmRotate(false);
      setRotateError(null);
      setDisplayCode(code);
    }
  }, [open, code]);

  const [copiedLink, setCopiedLink] = useState(false);
  async function copyLink() {
    try {
      await navigator.clipboard.writeText(pathToShareLink(`/invite/${displayCode}`));
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      toast.error(err, 'Could not copy');
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(displayCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  async function doRotate() {
    setRotating(true);
    setRotateError(null);
    try {
      const r = await api.rotateInvite(serverId);
      setDisplayCode(r.inviteCode);
      setConfirmRotate(false);
    } catch (err) {
      setRotateError(err instanceof ApiError ? err.message : 'Failed to rotate');
    } finally {
      setRotating(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Invite Code">
      <div className="space-y-4">
        <p className="text-sm text-text-muted">
          Share this code with friends so they can join <strong className="text-text">{serverName}</strong>.
        </p>
        <div className="rounded-xl border border-border-soft bg-elevated p-4 flex items-center justify-center gap-3">
          <span className="font-mono text-2xl font-bold tracking-widest text-text">{displayCode}</span>
          <Button onClick={copy} title="Copy">
            <Icon name="copy" size={14} /> {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
        <div className="rounded-xl border border-border-soft bg-elevated p-3 flex items-center gap-2 min-w-0">
          <span className="flex-1 min-w-0 font-mono text-xs text-text truncate" title={pathToShareLink(`/invite/${displayCode}`)}>
            {pathToShareLink(`/invite/${displayCode}`)}
          </span>
          <Button onClick={copyLink} title="Copy invite link">
            <Icon name="copy" size={14} /> {copiedLink ? 'Copied!' : 'Copy link'}
          </Button>
        </div>
        <div className="text-xs text-text-subtle text-center">
          Send the link (it shows a preview in most apps), or have friends paste the code in the "Join Server" dialog.
        </div>

        {canRotate && (
          <div className="border-t border-border-soft pt-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-text-subtle font-bold">
              Security
            </div>
            <p className="text-xs text-text-muted">
              If this code has been shared somewhere it shouldn't have, rotate it.
              The current code becomes invalid immediately. Existing members are not removed.
            </p>
            {rotateError && (
              <div className="text-xs text-danger bg-danger/10 px-2 py-1 rounded-sm">
                {rotateError}
              </div>
            )}
            {!confirmRotate ? (
              <Button
                onClick={() => setConfirmRotate(true)}
                className="text-danger! hover:bg-danger/10!"
              >
                Rotate invite code
              </Button>
            ) : (
              <div className="rounded-xl border border-border-soft bg-elevated p-3 space-y-2">
                <div className="text-xs text-text">
                  Are you sure? Anyone using the current code will need the new one.
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={doRotate}
                    disabled={rotating}
                    className="text-danger!"
                  >
                    {rotating ? 'Rotating…' : 'Yes, rotate'}
                  </Button>
                  <Button onClick={() => setConfirmRotate(false)} disabled={rotating}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}

// =============================================================================
// ServerSettingsModal — owner/admin-only: server icon, name (later)
// =============================================================================
interface ServerSettingsModalProps {
  open: boolean;
  onClose: () => void;
  serverId: string;
  serverName: string;
  currentIconUrl: string | null;
  onUpdated: (changes: { iconUrl?: string | null; name?: string }) => void;
}

export function ServerSettingsModal({
  open,
  onClose,
  serverId,
  serverName,
  currentIconUrl,
  onUpdated,
}: ServerSettingsModalProps) {
  const [name, setName] = useState(serverName);
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // Re-sync the field whenever the modal opens or the server gets renamed
  // elsewhere (e.g. a live server.updated from another session).
  useEffect(() => {
    if (open) {
      setName(serverName);
      setNameError(null);
    }
  }, [open, serverName]);

  const trimmed = name.trim();
  const nameDirty = trimmed.length > 0 && trimmed !== serverName;

  async function saveName() {
    if (!nameDirty) return;
    setSavingName(true);
    setNameError(null);
    try {
      await api.updateServer(serverId, { name: trimmed });
      onUpdated({ name: trimmed });
    } catch (err) {
      setNameError(err instanceof ApiError ? err.message : 'Failed to rename server');
    } finally {
      setSavingName(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Settings — ${serverName}`} width="w-[460px]">
      <div className="space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-2">
            Server name
          </div>
          <div className="flex items-end gap-2">
            <Input
              className="flex-1"
              value={name}
              maxLength={64}
              placeholder="Server name"
              autoFocus
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void saveName();
                }
              }}
              error={nameError ?? undefined}
            />
            <Button
              variant="primary"
              onClick={saveName}
              disabled={!nameDirty || savingName}
            >
              {savingName ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-2">
            Server icon
          </div>
          <ImageUploader
            name={serverName}
            currentUrl={currentIconUrl}
            size={72}
            hint="Shown in everyone's server list. Square images work best."
            onUpload={async (file) => {
              const url = await uploadServerIcon(serverId, file);
              onUpdated({ iconUrl: url });
            }}
            onRemove={async () => {
              await api.removeServerIcon(serverId);
              onUpdated({ iconUrl: null });
            }}
          />
        </div>

        <div className="flex justify-end pt-1">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
